import React, { useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import cx from 'classnames';
import { Button, Icon, Menu } from '@storable/canaveral-components';
import useDropdown from '../../hooks/useDropdown';
import NavigationLinkForm from '../NavigationLinkForm';
import NavigationGroupForm from '../NavigationGroupForm';
import NavigationPageForm from '../NavigationPageForm';
import PageTitle from '../PageTitle';
import isDescendant from './isDescendant';
import getNavigationItemIcon from './getNavigationItemIcon';
import styles from './styles.scss';

const propTypes = {
  canDrag: PropTypes.bool,
  canDrop: PropTypes.bool,
  className: PropTypes.string,
  connectDragPreview: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  didDrop: PropTypes.bool.isRequired,
  draggedNode: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isDragging: PropTypes.bool.isRequired,
  listIndex: PropTypes.number.isRequired,
  lowerSiblingCounts: PropTypes.arrayOf(PropTypes.number).isRequired,
  node: PropTypes.shape({
    children: PropTypes.arrayOf(
      PropTypes.object.isRequired,
    ),
    expanded: PropTypes.bool.isRequired,
    navigationItem: PropTypes.shape({
      id: PropTypes.string,
      link: PropTypes.object,
      page: PropTypes.object,
      text: PropTypes.string,
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onMoveToBottom: PropTypes.func.isRequired,
    onMoveToTop: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
  }).isRequired,
  parentNode: PropTypes.shape({
    children: PropTypes.arrayOf(
      PropTypes.shape({
        navigationItem: PropTypes.shape({
          id: PropTypes.string,
        }).isRequired,
      }).isRequired,
    ),
  }),
  path: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  toggleChildrenVisibility: PropTypes.func,
  treeIndex: PropTypes.number.isRequired,
};

const defaultProps = {
  canDrag: false,
  canDrop: false,
  className: undefined,
  draggedNode: undefined,
  parentNode: undefined,
  style: {},
  toggleChildrenVisibility: undefined,
};

// See https://github.com/frontend-collective/react-sortable-tree#themes for
// more information on the props available to this component.
const NavigationTreeNodeContent = React.memo(({
  canDrag,
  canDrop,
  className,
  connectDragPreview,
  connectDragSource,
  didDrop,
  draggedNode,
  isDragging,
  listIndex,
  lowerSiblingCounts,
  node,
  parentNode,
  path,
  style,
  toggleChildrenVisibility,
  treeIndex,
}) => {
  const {
    canMoveToBottom,
    children,
    expanded,
    hasPrimaryButton,
    hasSecondaryButton,
    navigationItem,
    onEdit,
    onMoveToBottom,
    onMoveToTop,
    onRemove,
    onToggleButton,
  } = node;

  const {
    isPrimary,
    isSecondary,
    link,
    page,
  } = navigationItem;

  const icon = getNavigationItemIcon(navigationItem);

  const isDraggedDescendant = draggedNode && isDescendant(draggedNode, node);
  const isLandingPadActive = !didDrop && isDragging;

  const collapseAriaLabel = expanded ? 'Collapse' : 'Expand';
  const collapseClasses = cx(
    styles.collapse,
    expanded && styles.collapsed,
  );

  const onCollapseClick = () => toggleChildrenVisibility({
    node,
    path,
    treeIndex,
  });

  const wrapperStyle = { height: '100%' };
  const wrapperClasses = cx(
    styles.wrapper,
    !canDrag && styles.wrapperDragDisabled,
  );

  const rowClasses = cx(
    styles.row,
    isLandingPadActive && styles.rowLandingPad,
    isLandingPadActive && !canDrop && styles.rowCancelPad,
    className,
  );

  const rowStyle = {
    opacity: isDraggedDescendant ? 0.5 : 1,
    ...style,
  };

  const moreNodesBelow = lowerSiblingCounts[lowerSiblingCounts.length - 1] > 0;
  const firstNode = parentNode
    ? parentNode.children[0].navigationItem.id === navigationItem.id
    : listIndex === 0;

  const [editing, setEditing] = useState();
  const [ref, show, setShow] = useDropdown({
    onHide: () => setEditing(false),
  });

  const onControlsToggle = () => {
    setShow(!show);
  };

  const onEditClick = () => {
    setEditing(true);
  };

  const onMoveToTopClick = () => {
    setShow(false);
    onMoveToTop(navigationItem.id);
  };

  const onMoveToBottomClick = () => {
    setShow(false);
    onMoveToBottom(navigationItem.id);
  };

  const onRemoveClick = () => {
    setShow(false);
    onRemove(navigationItem.id);
  };

  const onToggleSecondary = () => {
    setShow(false);
    onToggleButton(navigationItem, !isSecondary, 'isSecondary');
  };

  const onTogglePrimary = () => {
    setShow(false);
    onToggleButton(navigationItem, !isPrimary, 'isPrimary');
  };

  const onCancel = () => {
    setEditing(false);
  };

  const onSubmit = (newNavigationItem) => {
    // eslint-disable-next-line no-param-reassign
    newNavigationItem.text = newNavigationItem.text.trim();
    setEditing(false);
    setShow(false);
    onEdit({ ...navigationItem, ...newNavigationItem });
  };

  let EditForm = NavigationGroupForm;
  if (navigationItem.link) {
    EditForm = NavigationLinkForm;
  } else if (navigationItem.page) {
    EditForm = NavigationPageForm;
  }

  const classes = cx(
    styles.controls,
    show && styles.show,
  );

  const buttonEligible = path.length === 1
    && (link || page) && (!children || !children.length);

  const showPrimaryOption = buttonEligible
    && (!hasPrimaryButton || isPrimary) && !isSecondary;

  const showSecondaryOption = buttonEligible
    && (!hasSecondaryButton || isSecondary) && !isPrimary;

  const showMoveToTop = !firstNode && !isPrimary && !isSecondary;

  const showMoveToBottom = moreNodesBelow && !isPrimary && !isSecondary && canMoveToBottom;

  const content = (
    <div
      className={wrapperClasses}
      style={wrapperStyle}
    >
      {connectDragPreview(
        <div className={rowClasses} style={rowStyle}>
          {toggleChildrenVisibility && children && children.length > 0 && (
            <button
              aria-label={collapseAriaLabel}
              className={collapseClasses}
              onClick={onCollapseClick}
              type="button"
            >
              <Icon
                className={styles.caret}
                id="filter-arrow-down"
                label={collapseAriaLabel}
              />
            </button>
          )}
          <div className={styles.content}>
            {icon && (
              <div className={styles.pre}>
                <Icon
                  className={styles.icon}
                  id={icon.id}
                  label={icon.label}
                />
              </div>
            )}
            {navigationItem.page ? (
              <PageTitle
                className={styles.title}
                page={navigationItem.page}
                titleOverride={navigationItem.text}
              />
            ) : (
              <div className={styles.title}>
                {navigationItem.text}
              </div>
            )}
            <div className={styles.buttonContainer}>
              {isSecondary && (
                <div className={styles.secondaryButton}>
                  Secondary Button
                </div>
              )}
              {isPrimary && (
                <div className={styles.primaryButton}>
                  Primary Button
                </div>
              )}
            </div>
          </div>
          <div
            className={classes}
            ref={ref}
          >
            <Button
              className={styles.toggle}
              context="bare"
              icon="edit2"
              label="Toggle Controls"
              onClick={onControlsToggle}
            />
            <div className={styles.menu}>
              {editing ? (
                <div className={styles.edit}>
                  <EditForm
                    navigationItem={navigationItem}
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                  />
                </div>
              ) : (
                <Menu>
                  <Menu.Item
                    componentClass="button"
                    onClick={onEditClick}
                  >
                    Edit
                  </Menu.Item>
                  {showMoveToTop && (
                    <Menu.Item
                      componentClass="button"
                      onClick={onMoveToTopClick}
                    >
                      Move to Top
                    </Menu.Item>
                  )}
                  {showMoveToBottom && (
                    <Menu.Item
                      componentClass="button"
                      onClick={onMoveToBottomClick}
                    >
                      Move to Bottom
                    </Menu.Item>
                  )}
                  {showSecondaryOption && (
                    <Menu.Item
                      componentClass="button"
                      onClick={onToggleSecondary}
                    >
                      {`${isSecondary ? 'Remove as' : 'Set as'} Secondary Button`}
                    </Menu.Item>
                  )}
                  {showPrimaryOption && (
                    <Menu.Item
                      componentClass="button"
                      onClick={onTogglePrimary}
                    >
                      {`${isPrimary ? 'Remove as' : 'Set as'} Primary Button`}
                    </Menu.Item>
                  )}
                  <Menu.Item
                    componentClass="button"
                    onClick={onRemoveClick}
                  >
                    Remove
                  </Menu.Item>
                </Menu>
              )}
            </div>
          </div>
        </div>,
      )}
    </div>
  );

  return !show && canDrag
    ? connectDragSource(content, { dropEffect: 'copy' })
    : content;
});

NavigationTreeNodeContent.propTypes = propTypes;
NavigationTreeNodeContent.defaultProps = defaultProps;


NavigationTreeNodeContent.fragments = (() => {
  const page = gql`
    fragment NavigationTreeNodeContentPage on Page {
      locale
      ...PageTitlePage
    }

    ${PageTitle.fragments.page}
  `;

  const navigationItemFields = gql`
    fragment NavigationTreeNodeContentNavigationItemFields on NavigationItem {
      id
      text
      page {
        ...NavigationTreeNodeContentPage
      }
      ...NavigationLinkFormNavigationItem
      ...NavigationGroupFormNavigationItem
      ...NavigationPageFormNavigationItem
    }

    ${page}
    ${NavigationLinkForm.fragments.navigationItem}
    ${NavigationGroupForm.fragments.navigationItem}
    ${NavigationPageForm.fragments.navigationItem}
  `;

  return {
    navigationItem: gql`
      fragment NavigationTreeNodeContentNavigationItem on NavigationItem {
        ...NavigationTreeNodeContentNavigationItemFields
        navigationItems {
          ...NavigationTreeNodeContentNavigationItemFields
          navigationItems {
            ...NavigationTreeNodeContentNavigationItemFields
            navigationItems {
              ...NavigationTreeNodeContentNavigationItemFields
            }
          }
        }
      }

      ${navigationItemFields}
    `,
    page,
  };
})();

export default NavigationTreeNodeContent;
